import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, CircleOff, Users as UsersIcon } from 'lucide-react';

import { EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import {
  ControlPlaneDataSurface,
  ControlPlaneMetricStrip,
  ControlPlanePage,
  ControlPlaneSection,
} from '../../app/components/foundation/control-plane';

import {
  TenantTable,
  TenantCreateModal,
} from './components';

import { useTenants } from './hooks/useTenants';
import { useAnalytics } from '../analytics/hooks/useAnalytics';

import './styles/tenants.css';


export function TenantsPage() {

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const {
    tenants,
    meta,
    loading,
    error,
    refetch,
  } = useTenants(page, pageSize, search);
  const { analytics, loading: analyticsLoading } = useAnalytics();

  const totalPages = Math.max(1, Math.ceil(meta.total / pageSize));
  const safePage = Math.min(page, totalPages);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [page, pageSize, search]);

  function handleCreated() {
    setIsCreateOpen(false);
    refetch();
  }

  return (
    <>
      <ControlPlanePage
        className="vania-tenants-control-plane-page"
        title="مدیریت تننت‌ها"
        description="مشاهده و مدیریت نمایندگی‌ها (تننت‌ها) در پلتفرم."
        summary={analyticsLoading ? <TenantSummarySkeleton /> : (
            <ControlPlaneMetricStrip
            items={[
              { label: 'کل کاربران', value: analytics ? formatPersianNumber(analytics.overview.total_users) : '—', icon: <UsersIcon size={16} />, tone: 'info' },
              { label: 'کاربران فعال', value: analytics ? formatPersianNumber(analytics.overview.active_users) : '—', icon: <CheckCircle2 size={16} />, tone: 'success' },
              { label: 'کاربران مسدود شده', value: analytics ? formatPersianNumber(analytics.overview.blocked_users) : '—', icon: <CircleOff size={16} />, tone: 'danger' },
            ]}
          />
        )}
      >
        <ControlPlaneSection>
          <ControlPlaneDataSurface>
            {error && tenants.length === 0 ? (
              <p className="vania-async-state vania-async-state-error" role="alert">{error}</p>
            ) : null}
            <div className="vania-tenants-toolbar">
              <label className="vania-tenants-search">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => { setSearch(event.target.value); setPage(1); }}
                  placeholder="نام یا اسلاگ تننت"
                  aria-label="جست‌وجوی تننت"
                />
              </label>
              <button type="button" className="vania-tenants-action-button" onClick={() => setIsCreateOpen(true)}>
                افزودن تننت
              </button>
            </div>
            <div className="vania-tenants-table-region">
              {error && tenants.length === 0 ? null : !loading && tenants.length === 0 ? (
                <EmptyState message="تننتی برای نمایش وجود ندارد." />
              ) : loading && tenants.length === 0 ? (
                <TenantsTableSkeleton />
              ) : (
                <TenantTable tenants={tenants} scrollRef={tableScrollRef} />
              )}
            </div>
            <div className="vania-tenants-pagination-bar">
              <label className="vania-tenants-page-size">
                <span>تعداد در صفحه</span>
                <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} aria-label="تعداد تننت در هر صفحه">
                  <option value="20">۲۰</option>
                  <option value="25">۲۵</option>
                  <option value="50">۵۰</option>
                </select>
              </label>
              <nav className="vania-tenants-pagination" aria-label="صفحه‌بندی تننت‌ها">
                <button type="button" className="vania-tenants-pagination__control" disabled={safePage === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>‹</button>
                <div className="vania-tenants-pagination__pages">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((item) => (
                    <button type="button" key={item} className={`vania-tenants-pagination__page${item === safePage ? ' is-active' : ''}`} aria-current={item === safePage ? 'page' : undefined} onClick={() => setPage(item)}>
                      {formatPersianNumber(item)}
                    </button>
                  ))}
                </div>
                <button type="button" className="vania-tenants-pagination__control" disabled={safePage === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>›</button>
              </nav>
            </div>
          </ControlPlaneDataSurface>
        </ControlPlaneSection>
      </ControlPlanePage>

      <TenantCreateModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}

function TenantSummarySkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری خلاصه کاربران">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 vania-tenants-summary-skeleton" aria-hidden="true">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={`tenant-summary-skeleton-${index}`} className="vania-tenants-summary-card">
            <VaniaContentSkeleton style={{ width: '42%', height: '0.75rem' }} />
            <VaniaContentSkeleton style={{ width: '68%', height: '1.2rem' }} />
          </div>
        ))}
      </div>
    </VaniaLoadingSystem>
  );
}

function TenantsTableSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" label="در حال بارگذاری تننت‌ها">
      <div className="vania-tenants-table-scroll" aria-hidden="true">
        <table className="vania-tenants-table">
          <thead>
            <tr>
              {['نام تننت', 'دامنه', 'موبایل نماینده', 'وضعیت', 'پلن', 'کاربران', 'سرویس‌ها', 'عملیات'].map((heading) => (
                <th key={heading}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }, (_, rowIndex) => (
              <tr key={`tenant-skeleton-${rowIndex}`}>
                {Array.from({ length: 8 }, (_, cellIndex) => (
                  <td key={`tenant-skeleton-${rowIndex}-${cellIndex}`}>
                    <VaniaContentSkeleton style={{ width: cellIndex === 0 ? '70%' : '55%' }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </VaniaLoadingSystem>
  );
}
